<template>
  <div
    class="cta-card card card-raised margin-horizontal margin-top"
    :class="[
      'cta-card--' + variant,
      { 'cta-card--gradient': gradient },
    ]"
  >
    <div v-if="icon" class="cta-card__icon">
      <f7-icon :f7="icon" :size="iconSize" />
    </div>

    <h3 class="cta-card__title">{{ title }}</h3>
    <p v-if="description" class="cta-card__desc">{{ description }}</p>

    <div v-if="variant === 'prompter' && progress != null" class="cta-card__progress">
      <div class="cta-card__progress-bar">
        <div
          class="cta-card__progress-fill"
          :style="{ width: Math.min(100, Math.max(0, progress)) + '%' }"
        />
      </div>
      <span class="cta-card__progress-label">{{ Math.round(progress) }}%</span>
    </div>

    <div :class="buttonLabel && linkLabel ? 'cta-card__actions cta-card__actions--split' : 'cta-card__actions'">
      <f7-button
        v-if="buttonLabel"
        fill round
        large
        :color="buttonColor"
        class="cta-card__btn"
        @click="$emit('action')"
        v-html="buttonLabel"
      >
      </f7-button>
      <f7-link
        v-if="linkLabel"
        class="cta-card__link"
        @click="$emit('link')"
      >
        {{ linkLabel }}
      </f7-link>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
const props = defineProps({
  variant: {
    type: String,
    default: 'hero',
    validator: (v) => ['hero', 'prompter', 'catalyst'].includes(v),
  },
  icon: { type: String, default: '' },
  title: { type: String, required: true },
  description: { type: String, default: '' },
  buttonLabel: { type: String, default: '' },
  linkLabel: { type: String, default: '' },
  buttonColor: { type: String, default: 'primary' },
  progress: { type: Number, default: null },
  gradient: { type: Boolean, default: false },
})

defineEmits(['action', 'link'])

const iconSize = computed(() => {
  if (props.variant === 'hero') return 60
  return 24
})
</script>

<style scoped>
.cta-card {
  background: var(--f7-page-bg-color, #fff);
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin: 0 0px 12px;
}

.cta-card--gradient {
  background: linear-gradient(135deg, var(--avocash-green-light, #EAF2EB) 0%, var(--f7-page-bg-color, #fff) 100%);
  border-color: transparent;
  box-shadow: none;
}

/* ─── Hero ─── */
.cta-card--hero {
  text-align: center;
  padding: 28px 20px;
}

.cta-card--hero .cta-card__icon {
  margin-bottom: 12px;
  color: var(--avocash-green, #1A6B42);
}

.cta-card--hero .cta-card__title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px;
  color: var(--f7-text-color, #000);
}

.cta-card--hero .cta-card__desc {
  font-size: 14px;
  line-height: 1.5;
  color: var(--f7-text-color, #333);
  margin: 0 0 16px;
}

/* ─── Prompter ─── */
.cta-card--prompter .cta-card__icon {
  color: var(--avocash-green, #1A6B42);
  margin-bottom: 8px;
}

.cta-card--prompter .cta-card__title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 6px;
  color: var(--f7-text-color, #000);
}

.cta-card--prompter .cta-card__desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #6d6d72);
  margin: 0 0 12px;
}

.cta-card__progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.cta-card__progress-bar {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--f7-list-item-border-color, #e0e0e0);
  overflow: hidden;
}

.cta-card__progress-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--avocash-green, #1A6B42);
  transition: width 0.4s ease;
}

.cta-card__progress-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--avocash-green, #1A6B42);
  min-width: 30px;
  text-align: right;
}

/* ─── Catalyst ─── */
.cta-card--catalyst {
  text-align: center;
  background: var(--f7-page-bg-color, #fff);
  border-style: dashed;
}

.cta-card--catalyst .cta-card__title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 6px;
}

.cta-card--catalyst .cta-card__desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #6d6d72);
  margin: 0 0 14px;
}

/* ─── Shared actions ─── */
.cta-card__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.cta-card__actions--split {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

@media (min-width: 480px) {
  .cta-card__actions--split {
    grid-template-columns: 1fr 1fr;
  }
}

.cta-card--hero .cta-card__actions {
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-card__btn {
  --f7-button-bg-color: var(--avocash-green, #1A6B42);
  --f7-button-hover-bg-color: var(--avocash-green-dark, #145432);
  min-height: 48px;
  min-width: 220px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 600;
  text-transform: none;
}

.cta-card__link {
  font-size: 14px;
  color: var(--avocash-green, #1A6B42);
  font-weight: 600;
}

/* ─── Responsive ─── */
@media (max-width: 480px) {
  .cta-card {
    margin: 0 8px 10px;
    padding: 16px;
    border-radius: 10px;
  }

  .cta-card--hero .cta-card__title {
    font-size: 18px;
  }
}
</style>
